import { makeAutoObservable, runInAction } from 'mobx'
import {
	ChatHttpService,
	ChatTurnStreamError,
	createChatRequestId,
	type ChatServiceBuildMetadata,
	type ChatSessionResponse,
	type WorkoutPreviewRecord,
} from '@entities/AIMessenger/api'
import {
	assistantMessageFromTurn,
	buildTurnPayload,
	type CoachChatCache,
	type CoachChatMessage,
	createEmptyChatCache,
	mergeChatMessages,
	messageFromRecord,
	messagesAfterLatestSeparator,
	type PinnedChatContext,
	replaceChatMessage,
	updatePreviewInCache,
} from '@entities/AIMessenger/model/coachChat'
import { LocalStorageKeys } from '@shared/constants/LocalStorageKeys'

export type ChatConnectionState = 'connecting' | 'ready' | 'sending' | 'error'
export type CoachChatFooterBadgeKind = 'typing' | 'answer' | 'action' | 'error'

type AuthConfig = {
	accessToken: string | null
	ensureFreshSession: () => Promise<string | null>
	initialized: boolean
	authenticated: boolean
}

type SendTextOptions = {
	showUserMessage?: boolean
	pinnedContext?: PinnedChatContext | null
	requestId?: string
	requestPayload?: ReturnType<typeof buildTurnPayload>
}

const SESSION_STORAGE_KEY = LocalStorageKeys.coachChatVisibleSessionId
const INITIAL_MESSAGE_LIMIT = 30
const OLDER_MESSAGE_LIMIT = 30

export class CoachChatStore {
	public sessionId: string | null = readStoredSessionId()
	public session: ChatSessionResponse | null = null
	public lastChatEndpoint: string | null = null
	public lastChatService: ChatServiceBuildMetadata | null = null
	public cache: CoachChatCache = createEmptyChatCache()
	public state: ChatConnectionState = 'connecting'
	public nextBefore: number | null = null
	public hasMore = false
	public isLoadingOlder = false
	public isStartingNewSession = false
	public activePreviewAction: string | null = null
	public isVisible = false

	private accessToken: string | null = null
	private ensureFreshSession: () => Promise<string | null> = async () => null
	private initialized = false
	private authenticated = false
	private loadInitialPromise: Promise<void> | null = null
	private attentionKind: Exclude<CoachChatFooterBadgeKind, 'typing'> | null = null
	private inFlightPendingMessageId: string | null = null
	private inFlightUserMessageId: string | null = null
	private inFlightStartedAt: string | null = null

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	public get messages(): CoachChatMessage[] {
		return this.cache.orderedIds.map(id => this.cache.byId[id]).filter(Boolean)
	}

	public get activeMessages(): CoachChatMessage[] {
		return messagesAfterLatestSeparator(this.messages)
	}

	public get latestPendingPreview(): WorkoutPreviewRecord | null {
		return (
			[...this.activeMessages]
				.reverse()
				.map(message => message.preview)
				.find(
					(preview): preview is WorkoutPreviewRecord =>
						preview?.status === 'pending_approval'
				) ?? null
		)
	}

	public get isBusy(): boolean {
		return this.state === 'connecting' || this.state === 'sending' || this.isStartingNewSession
	}

	public get footerBadgeKind(): CoachChatFooterBadgeKind | null {
		if (this.isVisible) return null
		if (this.state === 'sending') return 'typing'
		return this.attentionKind
	}

	public configureAuth(config: AuthConfig): void {
		this.accessToken = config.accessToken
		this.ensureFreshSession = config.ensureFreshSession
		this.initialized = config.initialized
		this.authenticated = config.authenticated

		if (config.initialized && !config.authenticated) {
			this.reset()
		}
	}

	public setVisible(isVisible: boolean): void {
		this.isVisible = isVisible
		if (isVisible) {
			this.attentionKind = null
		}
	}

	public reset(): void {
		this.sessionId = null
		this.session = null
		this.lastChatEndpoint = null
		this.lastChatService = null
		this.cache = createEmptyChatCache()
		this.state = 'connecting'
		this.nextBefore = null
		this.hasMore = false
		this.isLoadingOlder = false
		this.isStartingNewSession = false
		this.activePreviewAction = null
		this.attentionKind = null
		this.loadInitialPromise = null
		this.inFlightPendingMessageId = null
		this.inFlightUserMessageId = null
		this.inFlightStartedAt = null
		storeSessionId(null)
	}

	public loadInitial(): Promise<void> {
		if (!this.initialized || !this.authenticated) return Promise.resolve()
		if (this.state === 'sending' && this.cache.orderedIds.length > 0) {
			return Promise.resolve()
		}
		if (this.loadInitialPromise) return this.loadInitialPromise

		this.loadInitialPromise = this.loadInitialInternal().finally(() => {
			runInAction(() => {
				this.loadInitialPromise = null
			})
		})
		return this.loadInitialPromise
	}

	public async loadOlderMessages(): Promise<void> {
		if (!this.sessionId || !this.hasMore || this.isLoadingOlder) return
		const token = await this.getAccessToken()
		if (!token) return

		runInAction(() => {
			this.isLoadingOlder = true
		})

		try {
			const page = await ChatHttpService.getMessagesPage({
				accessToken: token,
				sessionId: this.sessionId,
				beforeSequenceNo: this.nextBefore,
				limit: OLDER_MESSAGE_LIMIT,
			})
			const hydratedMessages = await this.hydrateMessages(token, page.items)
			runInAction(() => {
				this.cache = mergeChatMessages(this.cache, hydratedMessages, 'prepend')
				this.nextBefore = page.next_before ?? null
				this.hasMore = page.has_more
			})
		} finally {
			runInAction(() => {
				this.isLoadingOlder = false
			})
		}
	}

	public sendText(text: string, options: SendTextOptions = {}): boolean {
		const trimmed = text.trim()
		if (!trimmed || this.isBusy) return false

		void this.sendTextInternal(trimmed, options)
		return true
	}

	public retryLastTurn(pinnedContext?: PinnedChatContext | null): boolean {
		const lastUserMessage = [...this.messages]
			.reverse()
			.find(message => message.role === 'user')
		if (!lastUserMessage) return false
		const matchingFailedRequestId = [...this.messages]
			.reverse()
			.find(
				message =>
					message.role === 'error' &&
					message.requestId &&
					message.requestId === lastUserMessage.requestId
			)?.requestId
		const canReplayExactPayload = Boolean(
			matchingFailedRequestId && lastUserMessage.requestPayload
		)
		return this.sendText(lastUserMessage.text, {
			showUserMessage: false,
			pinnedContext: canReplayExactPayload ? null : pinnedContext,
			requestId: canReplayExactPayload ? (matchingFailedRequestId ?? undefined) : undefined,
			requestPayload: canReplayExactPayload ? lastUserMessage.requestPayload : undefined,
		})
	}

	public startNewSession(): boolean {
		if (this.isStartingNewSession || this.state === 'sending') return false
		void this.startNewSessionInternal()
		return true
	}

	public approvePreview(preview: WorkoutPreviewRecord): boolean {
		if (preview.status !== 'pending_approval' || this.activePreviewAction) return false
		void this.approvePreviewInternal(preview)
		return true
	}

	public cancelPreview(preview: WorkoutPreviewRecord): boolean {
		if (preview.status !== 'pending_approval' || this.activePreviewAction) return false
		void this.cancelPreviewInternal(preview)
		return true
	}

	public reconcileLatest(): Promise<void> {
		if (!this.sessionId || !this.initialized || !this.authenticated) {
			return Promise.resolve()
		}
		if (this.state === 'sending') {
			return this.reconcileInFlightTurn()
		}
		return this.loadInitial()
	}

	private async loadInitialInternal(): Promise<void> {
		try {
			if (this.cache.orderedIds.length === 0) {
				runInAction(() => {
					this.state = 'connecting'
				})
			}

			const token = await this.getAccessToken()
			if (!token) throw new Error('chat_auth_required')
			const activeSessionId = await this.resolveSession(token)
			const history = await this.loadSessionHistory(token, activeSessionId)
			const hydratedMessages = await this.hydrateMessages(token, history.items)

			runInAction(() => {
				if (hydratedMessages.length > 0 || this.cache.orderedIds.length === 0) {
					this.cache = mergeChatMessages(
						createEmptyChatCache(),
						hydratedMessages,
						'replace'
					)
				}
				this.nextBefore = history.nextBefore
				this.hasMore = history.hasMore
				this.state = 'ready'
			})
		} catch {
			runInAction(() => {
				this.state = 'error'
				if (!this.isVisible) this.attentionKind = 'error'
			})
		}
	}

	private async sendTextInternal(trimmed: string, options: SendTextOptions): Promise<void> {
		const showUserMessage = options.showUserMessage ?? true
		const token = await this.getAccessToken()
		if (!token) {
			runInAction(() => {
				this.state = 'error'
				if (!this.isVisible) this.attentionKind = 'error'
			})
			return
		}

		let activeSessionId: string
		try {
			activeSessionId = this.sessionId ?? (await this.resolveSession(token))
		} catch (error) {
			const errorMessage: CoachChatMessage = {
				id: createLocalId('error'),
				role: 'error',
				text: publicErrorText(error),
				createdAt: new Date().toISOString(),
				status: 'failed',
				errorCode: 'session_failed',
			}
			runInAction(() => {
				this.cache = mergeChatMessages(this.cache, [errorMessage], 'append')
				this.state = 'error'
				if (!this.isVisible) this.attentionKind = 'error'
			})
			return
		}

		const now = new Date().toISOString()
		const requestId = options.requestId ?? createChatRequestId()
		const requestPayload =
			options.requestPayload ?? buildTurnPayload(trimmed, options.pinnedContext ?? null)
		const optimisticId = createLocalId('user')
		const pendingId = createLocalId('assistant')
		const optimisticMessage: CoachChatMessage = {
			id: optimisticId,
			sessionId: activeSessionId,
			role: 'user',
			text: trimmed,
			createdAt: now,
			status: 'sending',
			requestId,
			requestPayload,
		}
		const pendingMessage: CoachChatMessage = {
			id: pendingId,
			sessionId: activeSessionId,
			role: 'assistant',
			text: '',
			createdAt: now,
			status: 'pending',
			requestId,
		}

		runInAction(() => {
			this.state = 'sending'
			this.attentionKind = null
			this.inFlightPendingMessageId = pendingId
			this.inFlightUserMessageId = showUserMessage ? optimisticId : null
			this.inFlightStartedAt = now
			this.cache = mergeChatMessages(
				this.cache,
				showUserMessage ? [optimisticMessage, pendingMessage] : [pendingMessage],
				'append'
			)
		})

		try {
			const turn = await ChatHttpService.sendTurn({
				accessToken: token,
				sessionId: activeSessionId,
				payload: requestPayload,
				requestId,
			})
			const assistantMessage = assistantMessageFromTurn(turn, turn.previewRecord ?? null)
			runInAction(() => {
				this.lastChatEndpoint = turn.transport?.chatEndpoint ?? this.lastChatEndpoint
				this.lastChatService = turn.transport?.service ?? this.lastChatService
				const withSentUser = showUserMessage
					? mergeChatMessages(
							this.cache,
							[{ ...optimisticMessage, status: 'sent' }],
							'append'
						)
					: this.cache
				this.cache = replaceChatMessage(withSentUser, pendingId, assistantMessage)
				this.state = assistantMessage.status === 'failed' ? 'error' : 'ready'
				this.clearInFlightTurn()
				if (!this.isVisible) {
					this.attentionKind =
						assistantMessage.preview?.status === 'pending_approval'
							? 'action'
							: 'answer'
				}
			})
		} catch (error) {
			const errorMessage: CoachChatMessage = {
				id: createLocalId('error'),
				sessionId: activeSessionId,
				turnId: error instanceof ChatTurnStreamError ? error.turnId : null,
				role: 'error',
				text: publicErrorText(error),
				createdAt: new Date().toISOString(),
				status: 'failed',
				errorCode: error instanceof ChatTurnStreamError ? error.errorCode : 'turn_failed',
				requestId:
					error instanceof ChatTurnStreamError
						? (error.requestId ?? requestId)
						: requestId,
				chatEndpoint: error instanceof ChatTurnStreamError ? error.chatEndpoint : null,
				chatService: error instanceof ChatTurnStreamError ? error.service : null,
			}
			runInAction(() => {
				this.lastChatEndpoint = errorMessage.chatEndpoint ?? this.lastChatEndpoint
				this.lastChatService = errorMessage.chatService ?? this.lastChatService
				const withFailedUser = showUserMessage
					? mergeChatMessages(
							this.cache,
							[{ ...optimisticMessage, status: 'sent' }],
							'append'
						)
					: this.cache
				this.cache = replaceChatMessage(withFailedUser, pendingId, errorMessage)
				this.state = 'error'
				this.clearInFlightTurn()
				if (!this.isVisible) this.attentionKind = 'error'
			})
		}
	}

	private async reconcileInFlightTurn(): Promise<void> {
		const token = await this.getAccessToken()
		if (!token || !this.sessionId) return

		try {
			const page = await ChatHttpService.getMessagesPage({
				accessToken: token,
				sessionId: this.sessionId,
				limit: INITIAL_MESSAGE_LIMIT,
			})
			const hydratedMessages = await this.hydrateMessages(token, page.items)
			const completedMessage = this.findCompletedInFlightMessage(hydratedMessages)

			runInAction(() => {
				let nextCache = mergeChatMessages(this.cache, hydratedMessages, 'append')
				if (completedMessage && this.inFlightPendingMessageId) {
					nextCache = replaceChatMessage(
						nextCache,
						this.inFlightPendingMessageId,
						completedMessage
					)
					if (this.inFlightUserMessageId) {
						const userMessage = nextCache.byId[this.inFlightUserMessageId]
						if (userMessage) {
							nextCache = mergeChatMessages(
								nextCache,
								[{ ...userMessage, status: 'sent' }],
								'append'
							)
						}
					}
					this.state = completedMessage.status === 'failed' ? 'error' : 'ready'
					this.clearInFlightTurn()
					if (!this.isVisible) {
						this.attentionKind =
							completedMessage.status === 'failed'
								? 'error'
								: completedMessage.preview?.status === 'pending_approval'
									? 'action'
									: 'answer'
					}
				}

				this.cache = nextCache
				this.nextBefore = page.next_before ?? this.nextBefore
				this.hasMore = page.has_more
			})
		} catch {
			// Keep the optimistic pending bubble; the original stream may still complete.
		}
	}

	private async startNewSessionInternal(): Promise<void> {
		const token = await this.getAccessToken()
		if (!token) {
			runInAction(() => {
				this.state = 'error'
				if (!this.isVisible) this.attentionKind = 'error'
			})
			return
		}

		runInAction(() => {
			this.isStartingNewSession = true
		})

		try {
			const session = await ChatHttpService.createSession(token)
			storeSessionId(session.id)
			const separator: CoachChatMessage = {
				id: createLocalId('session'),
				sessionId: session.id,
				role: 'separator',
				text: 'Новая сессия',
				createdAt: new Date().toISOString(),
				status: 'sent',
			}
			runInAction(() => {
				this.sessionId = session.id
				this.session = session
				this.lastChatEndpoint = session.transport?.chatEndpoint ?? null
				this.lastChatService = session.transport?.service ?? null
				this.nextBefore = null
				this.hasMore = false
				this.cache = mergeChatMessages(this.cache, [separator], 'append')
				this.state = 'ready'
			})
		} catch (error) {
			const errorMessage: CoachChatMessage = {
				id: createLocalId('error'),
				role: 'error',
				text: publicErrorText(error),
				createdAt: new Date().toISOString(),
				status: 'failed',
				errorCode: 'session_failed',
			}
			runInAction(() => {
				this.cache = mergeChatMessages(this.cache, [errorMessage], 'append')
				this.state = 'error'
				if (!this.isVisible) this.attentionKind = 'error'
			})
		} finally {
			runInAction(() => {
				this.isStartingNewSession = false
			})
		}
	}

	private async approvePreviewInternal(preview: WorkoutPreviewRecord): Promise<void> {
		const token = await this.getAccessToken()
		if (!token) return

		runInAction(() => {
			this.activePreviewAction = preview.id
			this.cache = updatePreviewInCache(this.cache, preview.id, {
				status: 'approving',
			})
		})

		try {
			const result = await ChatHttpService.approvePreview(token, preview)
			runInAction(() => {
				this.cache = updatePreviewInCache(this.cache, preview.id, {
					status: 'approved',
					created_workout_id: result.workout_id,
				})
				this.state = 'ready'
				this.attentionKind = null
			})
		} catch {
			runInAction(() => {
				this.cache = updatePreviewInCache(this.cache, preview.id, {
					status: 'requires_revalidation',
				})
				this.state = 'error'
				if (!this.isVisible) this.attentionKind = 'error'
			})
		} finally {
			runInAction(() => {
				this.activePreviewAction = null
			})
		}
	}

	private async cancelPreviewInternal(preview: WorkoutPreviewRecord): Promise<void> {
		const token = await this.getAccessToken()
		if (!token) return

		runInAction(() => {
			this.activePreviewAction = preview.id
		})

		try {
			await ChatHttpService.cancelPreview(token, preview.id)
			runInAction(() => {
				this.cache = updatePreviewInCache(this.cache, preview.id, {
					status: 'cancelled',
				})
				this.state = 'ready'
				this.attentionKind = null
			})
		} catch {
			runInAction(() => {
				this.state = 'error'
				if (!this.isVisible) this.attentionKind = 'error'
			})
		} finally {
			runInAction(() => {
				this.activePreviewAction = null
			})
		}
	}

	private async resolveSession(token: string): Promise<string> {
		const storedSessionId = readStoredSessionId()
		if (storedSessionId) {
			try {
				const session = await ChatHttpService.getSession(token, storedSessionId)
				storeSessionId(session.id)
				runInAction(() => {
					this.sessionId = session.id
					this.session = session
					this.lastChatEndpoint = session.transport?.chatEndpoint ?? this.lastChatEndpoint
					this.lastChatService = session.transport?.service ?? this.lastChatService
				})
				return session.id
			} catch {
				storeSessionId(null)
			}
		}

		let session
		try {
			session = await ChatHttpService.getCurrentSession(token)
		} catch {
			session = await ChatHttpService.createSession(token)
		}
		storeSessionId(session.id)
		runInAction(() => {
			this.sessionId = session.id
			this.session = session
			this.lastChatEndpoint = session.transport?.chatEndpoint ?? this.lastChatEndpoint
			this.lastChatService = session.transport?.service ?? this.lastChatService
		})
		return session.id
	}

	private async hydrateMessages(
		token: string,
		records: Parameters<typeof messageFromRecord>[0][]
	): Promise<CoachChatMessage[]> {
		const baseMessages = records
			.map(messageFromRecord)
			.filter((message): message is CoachChatMessage => message !== null)
		const previewIds = Array.from(
			new Set(
				baseMessages
					.map(message => message.previewId)
					.filter((id): id is string => Boolean(id))
			)
		)
		const previewEntries = await Promise.all(
			previewIds.map(async previewId => {
				try {
					return [previewId, await ChatHttpService.getPreview(token, previewId)] as const
				} catch {
					return [previewId, null] as const
				}
			})
		)
		const previewsById = Object.fromEntries(previewEntries)
		return baseMessages.map(message => ({
			...message,
			preview: message.previewId ? previewsById[message.previewId] : null,
		}))
	}

	private async loadSessionHistory(
		token: string,
		sessionId: string
	): Promise<{
		items: Parameters<typeof messageFromRecord>[0][]
		nextBefore: number | null
		hasMore: boolean
	}> {
		const items: Parameters<typeof messageFromRecord>[0][] = []
		const seenBefore = new Set<number>()
		let beforeSequenceNo: number | null = null
		let nextBefore: number | null = null
		let hasMore = false

		do {
			const page = await ChatHttpService.getMessagesPage({
				accessToken: token,
				sessionId,
				beforeSequenceNo,
				limit: INITIAL_MESSAGE_LIMIT,
			})
			items.push(...page.items)
			nextBefore = page.next_before ?? null
			hasMore = page.has_more && nextBefore !== null && !seenBefore.has(nextBefore)
			if (nextBefore !== null) {
				seenBefore.add(nextBefore)
			}
			beforeSequenceNo = nextBefore
		} while (hasMore)

		return { items, nextBefore: hasMore ? nextBefore : null, hasMore }
	}

	private findCompletedInFlightMessage(messages: CoachChatMessage[]): CoachChatMessage | null {
		if (!this.inFlightStartedAt) return null
		const startedAt = Date.parse(this.inFlightStartedAt)
		return (
			messages.find(message => {
				if (message.role !== 'assistant' && message.role !== 'error') return false
				const createdAt = Date.parse(message.createdAt)
				return Number.isNaN(startedAt) || Number.isNaN(createdAt) || createdAt >= startedAt
			}) ?? null
		)
	}

	private clearInFlightTurn(): void {
		this.inFlightPendingMessageId = null
		this.inFlightUserMessageId = null
		this.inFlightStartedAt = null
	}

	private async getAccessToken(): Promise<string | null> {
		return this.ensureFreshSession()
	}
}

const publicErrorText = (error: unknown): string => {
	const message = error instanceof Error ? error.message : ''
	if (message.includes('provider_unavailable')) {
		return 'Сейчас тренер недоступен. Сообщение можно повторить через минуту.'
	}
	return 'Не получилось получить ответ. Сообщение не потеряно, можно повторить.'
}

const createLocalId = (scope: string): string => {
	if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
		return `local:${scope}:${crypto.randomUUID()}`
	}
	return `local:${scope}:${Date.now()}:${Math.random().toString(16).slice(2)}`
}

const readStoredSessionId = (): string | null => {
	if (typeof window === 'undefined') return null
	return window.localStorage?.getItem(SESSION_STORAGE_KEY) ?? null
}

const storeSessionId = (sessionId: string | null): void => {
	if (typeof window === 'undefined') return
	if (sessionId) {
		window.localStorage?.setItem(SESSION_STORAGE_KEY, sessionId)
		return
	}
	window.localStorage?.removeItem(SESSION_STORAGE_KEY)
	window.sessionStorage?.removeItem(SESSION_STORAGE_KEY)
}

export const coachChatStore = new CoachChatStore()
